import React, { useEffect, useMemo, useState } from 'react'
import { BoxProps } from 'rebass'

import { MeQuery, useCardBalanceLazyQuery } from '../../types/graphql'
import SFlex from 'components/opinionless/SFlex'
import SIcon from 'components/opinionless/SIcon'

import CreditCardOutlineSvg from '@svgs/credit-card-outline.svg'
import SText from 'components/opinionless/SText'
import { useTranslation } from '../../i18n'
import SButton, { SButtonType } from 'components/opinionless/SButton'
import SLink from 'components/opinionless/SLink'
import { titleForTransferMethodType } from '../../utils/transferMethod'
import { formatMoney } from '../../utils/money'
import { useSTheme } from 'styles/theme'
import { LocalStorageKeys } from 'utils/localStorage'
import ConditionalRender from 'utils/conditionalRender'
import moment from 'moment'
import { useAuthenticatedUser } from '../../v2/context/user'

type Props = BoxProps

const HomePaymentMethod: React.FC<Props> = (props: Props) => {
    const { sx = {}, ...otherProps } = props
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()

    const [, { data }] = useCardBalanceLazyQuery()
    const { authenticatedUser, transferMethods } = useAuthenticatedUser()

    const localStorageBalance = JSON.parse(localStorage.getItem(LocalStorageKeys.CARD_BALANCE))
    const [balance, setBalance] = useState(localStorageBalance?.value || 0)

    const transferMethod = useMemo(() => {
        if (!authenticatedUser || !authenticatedUser.transferMethods.edges.length) {
            return null
        } else {
            const edge = authenticatedUser.transferMethods.edges.find(
                (transferMethodEdge: MeQuery['me']['transferMethods']['edges'][0]) =>
                    transferMethodEdge.node.isCurrentMethod
            )
            if (edge) {
                return edge.node
            } else {
                return null
            }
        }
    }, [authenticatedUser, transferMethods])

    const transferMethodType = useMemo(() => {
        if (!transferMethod) {
            return null
        } else {
            return titleForTransferMethodType(transferMethod.type, t)
        }
    }, [transferMethod, t])

    const transferMethodText = useMemo(() => {
        if (!transferMethod) {
            return null
        } else {
            return transferMethod.halfLiner
        }
    }, [transferMethod, t])

    const transferMethodAddress = useMemo(() => {
        if (transferMethod?.address1) {
            return `${transferMethod?.address1}, ${transferMethod?.address2 && `${transferMethod?.address2},`} ${
                transferMethod?.city
            }, ${transferMethod?.zipCode}`
        }
    }, [transferMethod, t])

    const shouldBeDisplayed = () => {
        return transferMethod?.type === 'PREPAID_CARD' && transferMethod?.isCurrentMethod
    }

    useEffect(() => {
        if (data?.cardBalance !== undefined && shouldBeDisplayed()) {
            localStorage.setItem(
                'balance',
                JSON.stringify({ value: data?.cardBalance, expirationDate: moment().add(1, 'hours') })
            )
            setBalance(data?.cardBalance)
        }
    }, [data])

    return (
        <SFlex
            sx={{
                flex: ['0 0 auto', '1 1 0', '1 1 0'],
                borderRadius: [0, 2, 2],
                boxShadow: 'main',
                bg: 'white',
                flexDirection: 'column',
                p: [0, 8, 8],
                ...sx,
            }}
            {...otherProps}
        >
            <SFlex
                flexDirection="row"
                alignItems="center"
                sx={{
                    px: [6, 0, 0],
                    py: [5, 0, 0],
                    mb: [0, 6, 6],
                    borderBottomStyle: 'solid',
                    borderBottomWidth: [0.2, 0, 0],
                    borderBottomColor: 'gray150',
                }}
            >
                <SIcon
                    display={['none', 'block', 'block']}
                    icon={CreditCardOutlineSvg}
                    size={20}
                    tintColor="black"
                    mr={4}
                />
                <SText
                    sx={{
                        fontWeight: 'bold',
                        fontSize: 4,
                        lineHeight: 1.4,
                        color: 'black',
                        mt: [2, 0, 0],
                        fontFamily: theme.fonts.default,
                    }}
                >
                    {t('home_payment_method')}{' '}
                    {transferMethod?.statusCode === 'Pre-Active' && `(${t('pre_active_text')})`}
                </SText>
            </SFlex>
            <SFlex
                flexDirection="row"
                sx={{
                    px: [6, 0, 0],
                    py: [6, 0, 0],
                }}
            >
                <SFlex flexDirection="column" flex="1 1 auto" mr={4}>
                    <SText
                        sx={{
                            fontSize: [4, 4, 4],
                            fontWeight: 'bold',
                            color: 'gray400',
                            mb: [1, 2, 2],
                        }}
                    >
                        {transferMethodType}
                        <ConditionalRender if={shouldBeDisplayed()}>
                            {` ( ${t('prepaid_card_available_balance')}`} {`${formatMoney(balance)} )`}
                        </ConditionalRender>
                    </SText>

                    <SText
                        sx={{
                            fontSize: 2,
                            color: 'gray400',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {transferMethodText}
                    </SText>
                    <SText
                        sx={{
                            fontSize: 2,
                            color: 'gray400',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            mt: [2],
                        }}
                    >
                        {transferMethodAddress}
                    </SText>
                    {transferMethod?.statusCode && (
                        <SText
                            sx={{
                                fontSize: 2,
                                color: 'gray400',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                mt: [2],
                            }}
                        >
                            Status: {transferMethod?.statusCode}
                        </SText>
                    )}
                </SFlex>
                <SLink href={`${window.POTENTIAL_USER?.program?.urlFragment || window.PROGRAM.urlFragment}/account`}>
                    <SButton
                        sx={{
                            height: 40,
                        }}
                        buttonType={SButtonType.Outline}
                    >
                        {t('transfer_method_edit_button')}
                    </SButton>
                </SLink>
            </SFlex>
        </SFlex>
    )
}

export default HomePaymentMethod
